/** @jsxImportSource ./simboluri-jsx */
import { useEffect, useRef, useState } from "react";
import { PAL_LAYERE } from "./paletaDesen";
import { BreakpointPas4, ModelResult, RandRigiditateNivel, StepState, fmtDate } from "./tipuri";
import { stepDrawingUrl } from "./apiStructura";
import { DrawingView } from "./DwgViewer";
import Info from "./Info";
import { Focus, hashBreakpoint, hashSectiune, nivelDinRef } from "./legaturi";
import TitluVerificare, { useCatalogVerificari } from "./Verificare";
import { DescriereaPasului } from "./TitluPas";

import TabelAvertismentePas4 from "./OpuriRefuzate";
import { numar, t, tn, tx, useT } from "../i18n";
import { TextMetoda } from "./Simbol";
// Pasul 4 — Modelul structural: propunerea de stâlpi (30×30) și grinzi
// (30×50) pe axele pereților persistenți (Pasul 2), cu o diagramă 2D per
// etaj (pereți simplificați + persistenți + structura). Modelul vectorial
// pentru OpenSeesPy pleacă direct spre pașii următori (nu se descarcă de
// aici — Stefan).
//
// Tabelele de DEPANARE (modificările venite din bucla externă a Pasului 19 și
// bugetul geometric al pereților) stau la FINALUL paginii: sunt utile la
// diagnosticarea unei iterații, nu la citirea propunerii.


// RIGIDITATEA LATERALĂ PE NIVEL, DIN GEOMETRIE (efectul de bici înainte de
// lanț). E aceeași mărime pe care Pasul 10 o verifică — rigiditatea de CADRU,
// D-ul lui Muto: D = a·12EI_c/h³, cu a din raportul k̄ al grinzilor de la nod
// față de stâlp —, calculată aici fără FEM, ca să se vadă înainte de lanț cât
// lipsește unui nivel retras. O bară per nivel și direcție, proporțională cu
// K cadru; raportul față de nivelul de dedesubt e roșu sub prag.
// Nivelurile se listează de sus în jos, ca o elevație.
function RigiditateNiveluri({
  rig,
}: {
  rig: NonNullable<ModelResult["rigiditate_niveluri"]>;
}) {
  useT();
  const directii = (["X", "Y"] as const).filter(
    (d) => (rig.directii?.[d] ?? []).length > 0);
  if (directii.length === 0) return null;
  const kMax = Math.max(
    1e-9,
    ...directii.flatMap((d) => rig.directii[d].map((r) => r.k_cadru_kn_mm ?? 0)));
  const nr = (x: number | null | undefined, z = 2) =>
    x == null || !isFinite(x) ? "—" : x.toFixed(z);
  const Rand = ({ r }: { r: RandRigiditateNivel }) => {
    const pica = r.raport != null && r.raport < rig.prag;
    const latime = Math.max(1, (100 * (r.k_cadru_kn_mm ?? 0)) / kMax);
    return (
      <tr className={!r.suprastructura ? "st-infra" : undefined}>
        <td style={{ whiteSpace: "nowrap" }}>
          {r.nivel} <span className="sg-muted">{r.nume}</span>
          {!r.suprastructura && (
            <span className="sg-muted"
                  title={t("struct.modl.nivel_infrastructura_sal")}>
              {" "}{t("struct.dr.infrastructura")}
            </span>
          )}
        </td>
        <td style={{ width: "40%" }}>
          <div title={t("struct.modl.k_cadru_titlu", { v: nr(r.k_cadru_kn_mm, 1) })}
               style={{ height: 12, borderRadius: 3, background: "var(--line)",
                        position: "relative" }}>
            <div style={{ position: "absolute", left: 0, top: 0, height: "100%",
                          width: `${latime}%`, borderRadius: 3,
                          background: pica ? "var(--bad)" : PAL_LAYERE.stalpi }} />
          </div>
        </td>
        <td className="sg-mono" style={{ whiteSpace: "nowrap" }}>
          {nr(r.k_cadru_kn_mm, 1)} kN/mm
        </td>
        <td className={`sg-mono ${pica ? "st-bad" : ""}`}
            title={r.raport == null ? t("struct.modl.primul_nivel")
                   : t("struct.modl.raport_titlu", { prag: nr(rig.prag) })}>
          {r.raport == null ? "—" : nr(r.raport, 3)}
          {pica && <span title={t("struct.modl.sub_prag_nivelul")}> ✗</span>}
        </td>
        <td className="sg-mono sg-muted" style={{ whiteSpace: "nowrap" }}
            title={t("struct.modl.12ei_h3_stalpii")}>
          {nr(r.k_proprie_kn_mm, 1)} kN/mm
        </td>
      </tr>
    );
  };
  return (
    <div className="st-findings">
      <h3>{t("struct.modl.rig_titlu")}</h3>
      <div className="sg-muted" style={{ maxWidth: 900 }}>
        <TextMetoda cheie="struct.modl.rig_text" valori={{ prag: nr(rig.prag) }} />
      </div>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginTop: 8 }}>
        {directii.map((d) => (
          <div key={d} style={{ flex: "1 1 420px", minWidth: 360 }}>
            <h4 style={{ margin: "6px 0" }}>{t("struct.arpl.directia_dir", { dir: d })}</h4>
            <table className="sg-table">
              <thead>
                <tr>
                  <th>{t("struct.top.nivel")}</th>
                  <th>{t("struct.dr.k_cadru")}</th>
                  <th />
                  <th title={t("struct.modl.raportul_rigiditate_cadr")}>K_i/K_i−1</th>
                  <th className="sg-muted">{t("struct.dr.k_proprie")}</th>
                </tr>
              </thead>
              <tbody>
                {[...rig.directii[d]]
                  .sort((a, b) => b.nivel - a.nivel)
                  .map((r) => <Rand key={r.nivel} r={r} />)}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

// O încălcare a regulilor de amplasare, ca REFERINȚĂ pe planșă. Planșa
// desenează stâlpii și grinzile cu sufixul de etaj („GR1.1"), fiindcă
// aceeași grindă există la fiecare nivel; pereții simplificați poartă
// id-ul lor, fără sufix. De asta traducerea nu e un simplu `element`.
function refIncalcare(
  inc: { element?: string; elemente?: string[]; nivel?: number },
  nivelCurent: number
): { ref: string; nivel: number } | null {
  const e = inc.element || (inc.elemente ?? [])[0];
  if (!e) return null;
  // regulile fără nivel propriu (grinda oblică față de grilă) se arată pe
  // etajul deschis acum — elementul există pe toate
  const nivel = inc.nivel ?? nivelCurent + 1;
  // «GR47» / «ST24» → entitatea etajului; orice altceva (pereți) rămâne cum e
  return { ref: /^(GR|ST)\d+$/.test(e) ? `${e}.${nivel}` : e, nivel };
}

// Lista completă a încălcărilor unei reguli, strânsă implicit. Până acum se
// vedeau primele 6, ca text mort: „33 de grinzi încalcă regula" nu spunea
// NICI care, NICI unde.
function ListaIncalcari({
  reg,
  projectId,
  solutie,
  nivelCurent,
  tinta,
  onSari,
}: {
  reg: {
    tip: string;
    incalcari: {
      element?: string;
      elemente?: string[];
      nivel?: number;
      nivel_nume?: string;
      detaliu?: string;
      [k: string]: unknown;
    }[];
  };
  projectId: string;
  solutie?: string;
  nivelCurent: number;
  tinta: string | null;
  onSari: (ref: string, nivel: number | null) => void;
}) {
  if (!reg.incalcari.length)
    return <span className="sg-muted">—</span>;
  return (
    <details open={reg.incalcari.length <= 6}>
      <summary className="sg-muted" style={{ cursor: "pointer" }}>
        {reg.incalcari.length === 1
          ? t("struct.modl.vezi_incalcarea")
          : t("struct.modl.vezi_cele_p1", { p1: reg.incalcari.length })}
      </summary>
      <ul className="md-incalcari">
        {reg.incalcari.map((inc, j) => {
          const tinta_ = refIncalcare(inc, nivelCurent);
          const nume =
            inc.nivel_nume ??
            (inc.nivel != null ? t("struct.dr.nivelul_n", { n: inc.nivel }) : null);
          const eticheta =
            inc.element || (inc.elemente ?? []).join(" + ") || "element";
          return (
            <li key={j} className={tinta_ && tinta === tinta_.ref ? "md-inc-activ" : undefined}>
              {tinta_ ? (
                <a
                  href={hashSectiune(projectId, "model_structura", solutie ?? null, {
                    ref: tinta_.ref,
                    nivel: tinta_.nivel,
                  })}
                  title={t("struct.modl.selecteaza_p1_plansa", { p1: tinta_.ref })}
                  onClick={(ev) => {
                    // aplicăm pe loc ȘI scriem hash-ul: al doilea click pe
                    // același rând n-ar mai produce `hashchange`, deci
                    // linkul ar părea mort
                    ev.preventDefault();
                    onSari(tinta_.ref, tinta_.nivel);
                    window.location.href = hashSectiune(
                      projectId, "model_structura", solutie ?? null,
                      { ref: tinta_.ref, nivel: tinta_.nivel }
                    );
                  }}
                >
                  {eticheta}
                </a>
              ) : (
                <span className="sg-mono">{eticheta}</span>
              )}
              {nume && <span className="sg-muted"> · {nume}</span>}
              {inc.detaliu && (
                <div className="sg-muted" style={{ fontSize: 12 }}>
                  {String(inc.detaliu)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

/** Traveele structurii față de modulul de parcare al proiectului: câte stau
 *  pe multiplii lui și câte valori distincte au — cele două cerințe pe care
 *  scorul le plătește la «grilă» (5 % fiecare, `interax.py`). */
function GrilaPeModul({ g, renuntati, rarire }: {
  g: NonNullable<ModelResult["interax"]>;
  renuntati?: ModelResult["stalpi_renuntati_pe_modul"];
  rarire?: ModelResult["rarire_pe_modul"];
}) {
  useT();
  const nr = (v: number | null | undefined, zec = 2) =>
    v == null ? "—" : numar(v, { maximumFractionDigits: zec });
  const afara = g.nr - g.pe_modul;
  const [aratRenuntati, setAratRenuntati] = useState(false);
  return (
    <div className="st-findings" data-testid="grila-pe-modul">
      <h3>
        {t("struct.modl.grila_titlu", { m: nr(g.modul_m) })}{" "}
        {afara === 0 ? (
          <span className="fem-ok">{t("struct.modl.toate_pe_modul", { n: g.nr })}</span>
        ) : (
          <span className="st-bad">
            {t("struct.modl.afara_modul", { a: afara, n: g.nr })}
          </span>
        )}
      </h3>
      <div className="sg-muted">
        {t("struct.modl.grila_text", {
          modul: nr(g.modul_m), tol: nr(g.toleranta_mm, 0),
          distincte: tn("struct.grila.distincte#n", g.nr_distincte),
        })}
        {g.dominanta_m != null && t("struct.modl.grila_dominanta", {
          m: nr(g.dominanta_m), n: g.dominanta_nr,
        })}
        {g.stalpi_intermediari_pe_modul
          ? t("struct.modl.grila_intermediari", { n: g.stalpi_intermediari_pe_modul })
          : null}.
      </div>
      <table className="sg-table">
        <thead>
          <tr>
            <th>{t("struct.grila.col_deschidere")}</th>
            <th>{t("struct.grila.col_travee")}</th>
            <th>{t("struct.modl.modul")}</th>
            <th>{t("struct.grila.col_grinzi")}</th>
          </tr>
        </thead>
        <tbody>
          {g.distincte.slice(0, 12).map((d) => (
            <tr key={d.L_m}>
              <td className="sg-mono">{nr(d.L_m)} m</td>
              <td className="sg-mono">{d.nr}</td>
              <td>{d.pe_modul
                ? <span className="fem-ok">✓ {d.locuri} × {nr(g.modul_m)}</span>
                : <span className="st-bad">✗</span>}</td>
              <td className="sg-mono" style={{ fontSize: 12 }}>
                {d.grinzi.slice(0, 8).join(", ")}{d.grinzi.length > 8 ? "…" : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {g.distincte.length > 12 && (
        <div className="sg-muted">{t("struct.modl.inca_valori", { n: g.distincte.length - 12 })}</div>
      )}
      {rarire?.linii_portante ? (
        <div className="sg-muted" style={{ marginTop: 8 }}
             data-testid="rarire-pe-modul">
          {t("struct.modl.rarire_text", {
            linii: rarire.linii_portante, noi: rarire.noi ?? 0,
            lipiti: rarire.lipiti ?? 0, schelet: rarire.schelet ?? 0,
          })}
          {renuntati?.length ? (
            <>
              {t("struct.modl.rarire_iar")}{" "}<button type="button" className="cmp-membri"
                            onClick={() => setAratRenuntati(!aratRenuntati)}>
                {t("struct.modl.rarire_capete", { n: renuntati.length })}
              </button>{" "}{t("struct.modl.rarire_fara_stalp")}
            </>
          ) : "."}
          {aratRenuntati && renuntati?.length ? (
            <div className="rc-chips" style={{ marginTop: 4 }}>
              {renuntati.map((s, i) => (
                <span key={i} className="rc-chip"
                      title={`${s.perete ?? "?"} · ${s.directie} · ${nr(s.L_perete_m)} m`}>
                  {s.perete ?? "?"}{s.pe_grinda ? t("struct.modl.p1", { p1: s.pe_grinda }) : t("struct.modl.fara_gazda")}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}


export default function StepModel({
  projectId,
  cloneId = null,
  rutaId,
  state,
  running,
  solutie,
  focus,
  breakpoints = [],
  bp = null,
}: {
  // de unde se CITESC datele: proiectul, plus clona soluției vizualizate
  projectId: string;
  cloneId?: string | null;
  // în ce proiect duc LINKURILE: cel real. Cele două diferă exact când te
  // uiți la o soluție — spațiul corpului poartă datele, dar ruta rămâne
  // `/p/<proiect>/structura/<soluție>`, altfel linkul copiat ar deschide
  // spațiul ca proiect de sine stătător
  rutaId: string;
  state: StepState | null;
  running: boolean;
  // referința rândului privit (v. `legaturi.refLegatura`), ca linkurile din
  // pagină să rămână pe aceeași soluție și pe același corp
  solutie?: string;
  // elementul cerut din afară (link dintr-o verificare picată a Pasului 19
  // sau din tabelul de încălcări de mai jos): „GR47.1"
  focus?: Focus;
  // ISTORICUL DE SOLUȚIONARE: pașii geometrici prin care trece Pasul 4, din
  // `result.breakpoints` al rândului; fiecare stare se cere cu `?bp=<k>`.
  breakpoints?: BreakpointPas4[];
  // starea înghețată de pe ecran (`?bp=`), dacă e una
  bp?: string | null;
}) {
  useT();
  const [nivel, setNivel] = useState(0);
  // entitatea selectată pe planșă; se schimbă din link, nu din clickul pe
  // desen (clickul selectează local, în DrawingView)
  const [tinta, setTinta] = useState<string | null>(null);
  const desenRef = useRef<HTMLDivElement>(null);

  // linkul din URL: deschide etajul cerut și selectează elementul, apoi
  // derulează la planșă. `focus.n` crește la fiecare navigare, deci al doilea
  // click pe același rând reaplică selecția.
  useEffect(() => {
    if (!focus?.ref) return;
    const niv = focus.nivel ?? nivelDinRef(focus.ref);
    if (niv != null && niv >= 1) setNivel(niv - 1);
    setTinta(focus.ref);
    // planșa se remontează la schimbarea etajului: derularea așteaptă cadrul
    // următor, altfel prinde poziția veche
    const t = window.setTimeout(
      () => desenRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      60
    );
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.n]);
  const catalog = useCatalogVerificari();

  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.modl.pasul_ruleaza")}</strong>
          <div className="sg-muted">
            {t("struct.modl.structura_propune_pereti")}
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.modl.nerulat")}
        {/* descrierea vine din catalog_pasi.py — același text cu
            cel de la hover pe titlul pasului */}
        <DescriereaPasului cheie="model_structura" />
      </div>
    );
  }

  const r = state.result as unknown as ModelResult;

  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">{t("struct.pas.esuat", { eroare: r?.error ?? t("struct.pas.eroare_necunoscuta") })}</div>
        <div className="sg-muted">{t("struct.modl.ruleaza_nou_meniul")}</div>
      </div>
    );
  }

  const et = r.etaje[Math.min(nivel, r.etaje.length - 1)];

  return (
    <div className="st-result">
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.modl.stalpi_lamele_propuse")}
            <Info
              titlu={t("struct.modl.unde_punem_stalpii")}
              ce={t("struct.modl.stalp_picior_masa")}
              deCe={t("struct.modl.grinda_duce_greutatea")}
              parametri={[
                { simbol: "30×30", nume: t("struct.modl.sectiunea_stalpului_cm"), sursa: t("struct.modl.presupusa_initial_pasul") },
                { simbol: "max 6 m", nume: t("struct.modl.distanta_maxima_intre"), sursa: t("struct.modl.regula_practica_document") },
              ]}
            />
          </div>
          <div className="st-card-value">{r.nr_stalpi_total}</div>
          <div className="st-card-sub">
            {r.modificari?.aplicate?.length
              ? t("struct.modl.30_30_cm", { p1: r.modificari.aplicate.length })
              : t("struct.modl.patrate_30")}
          </div>
        </div>
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.modl.grinzi_propuse")}
            <Info
              titlu={t("struct.modl.unde_punem_grinzile")}
              ce={t("struct.modl.grinda_bara_beton")}
              deCe={t("struct.modl.daca_grinda_sta")}
              parametri={[
                { simbol: "30×50", nume: t("struct.modl.latimea_inaltimea_grinzi"), sursa: t("struct.modl.presupusa_initial_pasul") },
                { simbol: t("struct.modl.simbol_axe"), nume: t("struct.modl.liniile_stau_grinzile"), sursa: t("struct.modl.peretii_persistenti_nive") },
              ]}
            />
          </div>
          <div className="st-card-value">{r.nr_grinzi_total}</div>
          <div className="st-card-sub">
            {t("struct.modl.fasii_30_50")}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.modl.etaje_modelate")}
            <Info
              titlu={t("struct.modl.unde_vin_inaltimile")}
              ce={t("struct.modl.etaj_model_are")}
              deCe={t("struct.modl.inaltimea_etajului_lungi")}
              parametri={[
                { simbol: "h_etaj", nume: t("struct.modl.inaltimea_fiecarui_etaj"), sursa: t("struct.modl.pasul_2_cotele") },
                { simbol: "z", nume: t("struct.modl.cota_fiecarui_planseu"), sursa: t("struct.modl.calculata_pas_inaltimile") },
                { simbol: "3 m", nume: t("struct.modl.inaltime_rezerva_folosit"), sursa: t("struct.modl.valoare_implicita_apare") },
              ]}
            />
          </div>
          <div className="st-card-value">{r.etaje.length}</div>
          <div className="st-card-sub">
            {t("struct.modl.regim_inaltimi", {
              regim: r.regim_inaltime || "?",
              h: r.etaje.map((e) => e.inaltime_m ?? "?").join(" / "),
            })}
          </div>
        </div>
      </div>

      {/* te uiți la o stare ÎNGHEȚATĂ din istoricul de soluționare (`?bp=`):
          pagina arată geometria de după pasul k, nu cea cu care merge lanțul;
          linkul din capăt duce înapoi la geometria livrată a rândului. */}
      {bp != null && (
        <div className="st-findings" style={{ borderLeft: "3px solid #a97614" }}>
          <h3>{t("struct.modl.bp_titlu", { bp })}</h3>
          <p className="sg-muted" style={{ margin: 0 }}>
            {r.solutie?.nume ? <strong>{r.solutie.nume}</strong> : null}
            {r.solutie?.nume ? " — " : ""}
            {r.solutie?.din_total
              ? t("struct.modl.bp_text_din", { bp, n: r.solutie.din_total })
              : t("struct.modl.bp_text", { bp })}{" "}
            <a href={hashSectiune(rutaId, "model_structura", solutie ?? null)}>
              {t("struct.modl.bp_inapoi")}
            </a>
            .
          </p>
        </div>
      )}

      <div className="st-findings">
        <h3>{t("struct.modl.presupuneri_initiale_rev")}</h3>
        {r.presupuneri.map((p, i) => (
          <div key={i} className="st-warn">• {p}</div>
        ))}
      </div>

      {r.ierarhie_pereti?.niveluri?.length ? (
        <div className="st-findings">
          <h3>{t("struct.modl.ierarhie_titlu")}</h3>
          <div className="sg-muted">{r.ierarhie_pereti.nota}</div>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.top.nivel")}</th>
                <th>{t("struct.modl.clasa")}</th>
                <th>{t("struct.modl.pereti_proprii_m")}</th>
                <th>{t("struct.modl.stalpi_noi")}</th>
                <th>{t("struct.modl.grinzi_noi")}</th>
                <th>{t("struct.modl.stalpi_plantati")}</th>
                <th>{t("struct.modl.stalpi_placa")}</th>
                <th>{t("struct.modl.stalpi_liberi")}</th>
                <th>{t("struct.modl.fara_structura_m")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t("struct.modl.toate")}</td>
                <td className="sg-mono">A</td>
                <td className="sg-mono">
                  {r.ierarhie_pereti.pereti_persistenti_m ?? "—"}
                </td>
                <td colSpan={6} className="sg-muted">
                  {t("struct.modl.pereti_persistenti_rand")}
                </td>
              </tr>
              {r.ierarhie_pereti.niveluri.map((n, i) => (
                <tr key={i}>
                  <td>{n.nivel}</td>
                  <td className="sg-mono">{n.clasa}</td>
                  <td className="sg-mono">{n.pereti_m}</td>
                  <td className="sg-mono">{n.stalpi_noi}</td>
                  <td className="sg-mono">{n.grinzi_noi}</td>
                  <td className="sg-mono">{n.plantati}</td>
                  <td className="sg-mono">{n.pe_placa ?? 0}</td>
                  <td className="sg-mono">{n.liberi ?? 0}</td>
                  <td className="sg-mono">{n.m_pierdut}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(r.ierarhie_pereti.avertismente ?? []).map((a, i) => (
            <div key={i} className="st-warn">• {a}</div>
          ))}
          {r.stalpi_plantati?.length ? (
            <>
              <div className="sg-muted" style={{ marginTop: 8 }}>
                {t("struct.modl.stalpii_plantati_element")}
              </div>
              <table className="sg-table">
                <thead>
                  <tr>
                    <th>{t("struct.modl.stalp")}</th>
                    <th>{t("struct.modl.exista_doar_nivelul")}</th>
                    <th>{t("struct.modl.plantat_grinda")}</th>
                  </tr>
                </thead>
                <tbody>
                  {r.stalpi_plantati.map((s, i) => (
                    <tr key={i}>
                      <td className="sg-mono">{s.id_plan}.{s.nivel}</td>
                      <td className="sg-mono">{s.nivel}</td>
                      <td className="sg-mono">
                        {s.pe_grinda}.{s.nivel - 1}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
          {r.stalpi_pe_placa?.length ? (
            <div className="st-warn" style={{ marginTop: 8 }}>
              {tx("struct.modl.pe_placa_text", {
                n: r.stalpi_pe_placa.length, lista: r.stalpi_pe_placa.join(", "),
              })}
            </div>
          ) : null}
          {r.stalpi_liberi?.length ? (
            <div className="sg-muted" style={{ marginTop: 8 }}>
              {tx("struct.modl.liberi_text", {
                n: r.stalpi_liberi.length, lista: r.stalpi_liberi.join(", "),
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* rezultatele salvate înainte de rigiditatea din geometrie n-au
          câmpul: panoul nu apare */}
      {r.rigiditate_niveluri?.directii && (
        <RigiditateNiveluri rig={r.rigiditate_niveluri} />
      )}

      {/* GRILA PE MODUL — lipsește pe rezultatele vechi */}
      {r.interax?.masurat && (
        <GrilaPeModul g={r.interax} renuntati={r.stalpi_renuntati_pe_modul}
                      rarire={r.rarire_pe_modul} />
      )}

      {/* SCHIȚA nu le-a rulat: se spune, nu se arată «toate respectate». */}
      {r.verificari_amplasare?.nerulata ? (
        <div className="st-findings">
          <h3>{t("struct.modl.verificarile_amplasare")}</h3>
          <div className="sg-muted">{r.verificari_amplasare.nota}</div>
        </div>
      ) : r.verificari_amplasare ? (
        <div className="st-findings">
          <h3>
            {t("struct.modl.verificarile_amplasare")}{" "}
            {r.verificari_amplasare.ok ? (
              <span className="fem-ok">{t("struct.modl.regulile_respectate")}</span>
            ) : (
              <span className="st-bad">
                ✗{" "}
                {tn("struct.modl.reguli_incalcate#n",
                    r.verificari_amplasare.reguli.filter((x) => !x.ok).length)}
              </span>
            )}
          </h3>
          <div className="sg-muted">{r.verificari_amplasare.nota}</div>
          <div className="sg-muted">
            {t("struct.modl.regula_incalcata_desfaso")}
          </div>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.modl.regula")}</th>
                <th>{t("verificari.col_verdict")}</th>
                <th>{t("struct.modl.incalcarile_una_cate")}</th>
              </tr>
            </thead>
            <tbody>
              {r.verificari_amplasare.reguli.map((reg, i) => (
                <tr key={i}>
                  {/* aceeași explicație la hover ca în tabelul Pasului 19,
                      unde categoria «amplasare» apare cu picatele ei */}
                  <td>
                    <TitluVerificare catalog={catalog} tip="amplasare"
                                     text={reg.regula} aratPasul={false} />
                  </td>
                  <td className={reg.ok ? "fem-ok" : "st-bad"}>
                    {reg.ok ? "✓" : `✗ ${reg.incalcari.length}`}
                  </td>
                  <td>
                    <ListaIncalcari
                      reg={reg}
                      projectId={rutaId}
                      solutie={solutie}
                      nivelCurent={Math.min(nivel, r.etaje.length - 1)}
                      tinta={tinta}
                      onSari={(ref, niv) => {
                        if (niv != null && niv >= 1) setNivel(niv - 1);
                        setTinta(ref);
                        desenRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="st-findings">
        <h3>{t("struct.modl.diagrama_etaj")}</h3>
        <div className="md-tabs">
          {r.etaje.map((e, i) => (
            <button
              key={i}
              className={`md-tab ${i === Math.min(nivel, r.etaje.length - 1) ? "active" : ""}`}
              onClick={() => setNivel(i)}
            >
              {e.nivel}
            </button>
          ))}
        </div>
        <div className="md-legend">
          <span>
            <span className="md-swatch" style={{ background: PAL_LAYERE.stalpi }} />{" "}
            {r.modificari?.aplicate?.length
              ? t("struct.modl.leg_stalpi_redim")
              : t("struct.modl.leg_stalpi")}
          </span>
          <span><span className="md-swatch" style={{ background: PAL_LAYERE.grinzi }} /> {t("struct.modl.leg_grinzi")}</span>
          <span><span className="md-swatch" style={{ background: PAL_LAYERE.peretiSimplificati }} /> {t("struct.modl.leg_pereti_simpl")}</span>
          <span><span className="md-swatch" style={{ background: PAL_LAYERE.peretiPersistenti }} /> {t("struct.modl.leg_pereti_pers")}</span>
          <span className="sg-muted">{t("struct.modl.leg_arhitectura")}</span>
          <span className="sg-muted">
            {t("struct.modl.leg_click")}
          </span>
        </div>
        <div className="st-finding">
          <span className="st-finding-label">{t("struct.modl.etaj")}</span>
          <div>
            <strong>{et.nivel}</strong> — <span className="sg-mono">{et.fisier}</span>
            {et.inaltime_m != null && (
              <div className="sg-muted">
                {t("struct.modl.inaltime_etaj", { h: et.inaltime_m })}
                {et.sursa_inaltime ? ` — ${et.sursa_inaltime}` : ""}
              </div>
            )}
            <div className="sg-muted">
              {t("struct.modl.etaj_rezumat", {
                s: et.nr_stalpi, a: et.stalpi_adaugati_6m, g: et.nr_grinzi,
                L: et.lungime_grinzi_m, d: et.deschidere_maxima_m,
              })}
              {(r.grinzi_scoase_paralele ?? 0) > 0 &&
                t("struct.modl.grinzi_scoase", { n: r.grinzi_scoase_paralele })}
              {(r.stalpi_uniti ?? 0) > 0 &&
                t("struct.modl.p1_perechi_stalpi", { p1: r.stalpi_uniti })}
              <Info
                titlu={t("struct.modl.regula_6m")}
                ce={t("struct.modl.daca_doi_stalpi")}
                deCe={t("struct.modl.grinda_scandura_intre")}
                parametri={[
                  { simbol: "6 m", nume: t("struct.modl.deschiderea_maxima_admis"), sursa: t("struct.modl.regula_practica_document2") },
                  { simbol: `${et.stalpi_adaugati_6m}`, nume: t("struct.modl.stalpi_adaugati_aceasta"), sursa: t("struct.modl.calculati_automat_pas") },
                ]}
              />
            </div>
            <div className="sg-muted">{et.motiv_layere}</div>
          </div>
        </div>
        <div className="sg-muted st-ran-at">{t("struct.pas.rulat_la", { cand: fmtDate(state.updated_at) })}</div>
      </div>

      <div className="st-preview" ref={desenRef}>
        <DrawingView
          key={nivel}
          src={stepDrawingUrl(projectId, "model_structura", Math.min(nivel, r.etaje.length - 1), cloneId)}
          title={t("struct.plan.model", { nivel: et.nivel })}
          embed
          // elementul cerut din tabelul de încălcări (sau dintr-un link al
          // Pasului 19): se selectează și se aduce în vizor, fără să schimbe
          // scara la care te uitai
          focusId={tinta}
        />
      </div>

      {(r.modificari?.aplicate?.length || r.modificari?.avertismente?.length) ? (
        <div className="st-findings">
          <h3>{t("struct.modl.modificari_titlu")}</h3>
          <div className="sg-muted">
            {t("struct.modl.peste_propunerea_automat")}
          </div>
          {r.modificari?.aplicate?.length ? (
            <table className="sg-table">
              <thead>
                <tr>
                  <th>Op</th>
                  <th>{t("verificari.col_element")}</th>
                  <th>{t("struct.tipver.col_detaliu")}</th>
                  <th>{t("struct.modl.sectiunea_rezultata_cm")}</th>
                  <th>{t("struct.modl.alunecare_cm")}</th>
                </tr>
              </thead>
              <tbody>
                {r.modificari.aplicate.map((a, i) => (
                  <tr key={i}>
                    <td className="sg-mono">{a.op ?? "redimensionare"}</td>
                    <td className="sg-mono">{a.id_plan}</td>
                    <td className="sg-mono">
                      {a.op === "adauga_stalp"
                        ? t("struct.modl.p1_p2_m", { p1: a.pe_grinda, p2: a.pozitie_m, p3: a.grinda_noua })
                        : a.op === "elimina_stalp"
                          ? (a.grinda_unita
                              ? t("struct.modl.grinzile_unesc_p1", { p1: a.grinda_unita, p2: a.deschidere_m })
                              : (a.nota ?? "—"))
                          : a.op === "adauga_grinda"
                            ? t("struct.modl.intre_p1_p2", { p1: (a.intre || []).join("–"), p2: a.L_m })
                            : a.op === "elimina_grinda"
                              ? (a.stalpi_orfani_eliminati?.length
                                  ? t("struct.modl.stalpi_orfani_eliminati", { p1: a.stalpi_orfani_eliminati.join(", ") })
                                  : "—")
                              : a.directie
                                ? `${a.directie} → ${a.L_cm} cm`
                                : "—"}
                    </td>
                    <td className="sg-mono">{a.sectiune ?? "—"}</td>
                    <td className="sg-mono">
                      {Array.isArray(a.alunecare_cm)
                        ? a.alunecare_cm.join(", ")
                        : (a.alunecare_cm ?? "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {r.modificari?.avertismente?.length ? (
            <>
              <div className="sg-muted" style={{ marginTop: 8 }}>
                {t("struct.modl.op_uri_sarite")}
              </div>
              {/* ACELAȘI tabel ca în raportul Pasului 19 (`OpuriRefuzate`) —
                  25 de propoziții lipite una de alta se citeau ca 25 de
                  probleme, deși erau 3 cauze */}
              <TabelAvertismentePas4 brute={r.modificari.avertismente} />
            </>
          ) : null}
        </div>
      ) : null}

      {r.capacitate_pereti?.stalpi ? (
        <div className="st-findings">
          <h3>{t("struct.modl.buget_titlu")}</h3>
          <div className="sg-muted">
            {tx("struct.modl.buget_text")}
          </div>
          {(() => {
            const st = r.capacitate_pereti!.stalpi;
            const randuri = Object.entries(st)
              .map(([id, v]) => ({
                id,
                v,
                rez: Math.min(
                  (v.X_max_cm ?? 0) - (v.actual_cm?.[0] ?? 0),
                  (v.Y_max_cm ?? 0) - (v.actual_cm?.[1] ?? 0),
                ),
              }))
              .sort((a, b) => a.rez - b.rez)
              .slice(0, 12);
            return (
              <table className="sg-table">
                <thead>
                  <tr>
                    <th>{t("struct.modl.stalp")}</th>
                    <th>{t("struct.modl.acum_xy")}</th>
                    <th>{t("struct.modl.maxim_x_cm")}</th>
                    <th>{t("struct.modl.maxim_y_cm")}</th>
                  </tr>
                </thead>
                <tbody>
                  {randuri.map(({ id, v }) => (
                    <tr key={id}>
                      <td className="sg-mono">{id}</td>
                      <td className="sg-mono">
                        {v.actual_cm?.[0]} × {v.actual_cm?.[1]}
                      </td>
                      <td className="sg-mono">{v.X_max_cm}</td>
                      <td className="sg-mono">{v.Y_max_cm}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
          <div className="sg-muted">
            {t("struct.modl.buget_nota", {
              n: Object.keys(r.capacitate_pereti.stalpi).length,
            })}
          </div>
        </div>
      ) : null}

      {/* ISTORICUL DE SOLUȚIONARE — la FINALUL paginii, ca breakpoint-urile
          să fie la îndemână fără să stea în drumul propunerii (
          «lasă în partea de jos a paginii un istoric de
          soluționare»). Cele N stări nu mai sunt soluții ale proiectului: se
          opreau la Pasul 4, iar numerele lor promiteau altceva. */}
      {breakpoints.length ? (
        <div className="st-findings">
          <h3>
            {t("struct.modl.istoric_titlu", { n: breakpoints.length })}
          </h3>
          <div className="sg-muted">
            {t("struct.modl.propunerea_naste_dintr")}
          </div>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.modl.th_pas")}</th>
                <th>{t("struct.modl.adauga")}</th>
                <th>{t("struct.modl.elemente_schimbate")}</th>
                <th>{t("struct.modl.refuzate_amplasare")}</th>
                <th>{t("struct.modl.stalpi")}</th>
                <th>{t("struct.grila.col_grinzi")}</th>
                <th>{t("struct.modl.starea_inghetata")}</th>
              </tr>
            </thead>
            <tbody>
              {breakpoints.map((b) => {
                const rez = b.rezultat ?? {};
                const acesta = r.solutie?.nr != null
                  && Number(r.solutie.nr) === b.k;
                return (
                  <tr key={b.k}>
                    <td className="sg-mono">
                      {b.k}
                      {b.livrata ? " ★" : ""}
                    </td>
                    <td>
                      <strong>{b.eticheta ?? "—"}</strong>
                      {b.motiv ? (
                        <div className="sg-muted">{b.motiv}</div>
                      ) : null}
                    </td>
                    {/* pasul 1 e punctul de plecare: n-are pas propriu, deci
                        nici cifre de schimbări */}
                    <td className="sg-mono">
                      {rez.aplicate != null ? rez.aplicate : "—"}
                    </td>
                    <td className="sg-mono">
                      {rez.refuzate ? rez.refuzate : "—"}
                    </td>
                    <td className="sg-mono">{rez.nr_stalpi ?? "—"}</td>
                    <td className="sg-mono">{rez.nr_grinzi ?? "—"}</td>
                    <td>
                      {b.stare === "esuata" ? (
                        <span className="st-bad">{t("struct.modl.a_esuat")}</span>
                      ) : acesta ? (
                        <span className="fem-ok">
                          {t("struct.modl.geometria_ecran")}
                        </span>
                      ) : b.model != null ? (
                        <a
                          href={hashBreakpoint(rutaId, solutie ?? null, String(b.k))}
                        >
                          {t("struct.modl.deschide_pasul", { k: b.k })}
                        </a>
                      ) : (
                        <span className="sg-muted">{t("struct.modl.fara_clona")}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="sg-muted">
            {t("struct.modl.istoric_nota")}
          </div>
        </div>
      ) : null}
    </div>
  );
}
